import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import EditIcon from '@mui/icons-material/Edit';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useUserRolePermissions } from 'hooks';
import * as R from 'ramda';
import { AuthContext } from 'authentication/AuthContext';
import { formatNumberWithoutDecimals } from 'utils/helpers/format';
import { useDispatch } from 'react-redux';
import { createSnack } from 'snackbar/snackbarSlice';
import { MDialog, StatusDetailsDialog } from 'common';
import Grid from '@mui/material/Grid2';
import DialogExplicitConfirmation from 'common/dialog-explicit-confirmation/DialogExplicitConfirmation';
import { dialogContainerStyles } from 'common/dialog-explicit-confirmation/DialogExplicitConfirmation.styles';
import { isBefore } from 'date-fns';
import Tooltip from '@mui/material/Tooltip';
import {
  useCancelBatchMutation,
  useCreateShipmentsMutation,
  useEditShipmentMutation,
  useGetBatchShipmentsQuery,
  useViewBatchQuery,
} from '../batchesEndpoints';
import BackButton from '../components/BackButton';
import DataDisplayItem from '../components/DataDisplayItem';
import NoAvailableCard from '../components/NoAvailableCard';
import { batchStatusTooltips, formatDate, isEditableStatus } from '../utils';
import EditDistributionsDrawer from '../DistributionsDrawer/EditDistributionsDrawer';

import ViewAssociatedShipments from './ViewAssociatedShipments';
import EditBatch from '../edit/EditBatch';
import StatusChip from '../components/StatusChip';

const containerStyle = {
  margin: '0 auto',
  maxWidth: '1470px',
  pb: 6,
  pt: 6,
  px: { lg: 3, xs: 2 },
  width: '100%',
};
const infoContainerStyle = {
  '&.MuiDialogContent-root': {
    py: 0,
  },
  display: 'grid',
  gap: { lg: 2, xs: 0 },
  gridTemplateColumns: { lg: '1fr 1fr' },
};

const ViewBatchById = () => {
  const { batchId } = useParams();
  const { isProductShippingManage } = useUserRolePermissions();
  const { affiliates } = useContext(AuthContext);
  const navigate = useNavigate();
  const childRef = useRef();
  const childRefShipment = useRef();
  const [isInvalidBatch, setIsInvalidBatch] = useState(true);
  const [hasNoUpdatesBatch, setHasNoUpdatesBatch] = useState(true);
  const { pathname, state } = useLocation();
  const onEditPage = pathname.toLowerCase().includes('/edit');
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });
  const params = { batchId };
  const [dialogType, setDialogType] = useState('');
  const [idToCancel, setIdToCancel] = useState(batchId);
  const [isExplicitDialogOpen, setIsExplicitDialogOpen] = useState(false);
  const dispatch = useDispatch();
  const [cancelBatch] = useCancelBatchMutation();
  const [newShipmentsValidated, setNewShipmentsValidated] = useState(false);
  const [hasNewShipments, setHasNewShipments] = useState(false);
  const [shipmentsEdited, setShipmentsEdited] = useState(false);
  const [isVolumeChangeDialogOpen, setIsVolumeChangeDialogOpen] = useState(false);
  const [isStatusDetailsDialogOpen, setIsStatusDetailsDialogOpen] = useState(false);
  const [editedTotalVolume, setEditedTotalVolume] = useState(0);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [selectedDistribution, setSelectedDistribution] = useState({});
  const [isSavingNewShipments, setIsSavingNewShipments] = useState(false);
  const [isSavingChanges, setIsSavingChanges] = useState(false);
  const openCancelPopup = () => {
    setIsExplicitDialogOpen(true);
  };

  const cancelSelectedBatch = () => {
    cancelBatch({ body: { batchId: idToCancel } })
      .then(res => {
        setIsExplicitDialogOpen(false);
        if (res.error) {
          dispatch(
            createSnack({
              message: res.error.data.title,
              options: { variant: 'error' },
            })
          );
        } else {
          dispatch(
            createSnack({
              message: res.data.message,
            })
          );
          setIsExplicitDialogOpen(false);
        }
      })
      .finally(() =>
        setTimeout(() => {
          setIdToCancel(null);
          setDialogType('');
        }, 500)
      );
  };

  const warningList = [
    'This process will also cancel all associated shipments.',
    'This action cannot be undone.',
  ];
  const additionalWarnings = [
    'You are removing the last shipment in this batch.',
    'This will make the Batch invalid until at least one shipment is added.',
  ];

  const {
    data: batchData = {},
    isFetching: isFetchingBatch,
    refetch,
  } = useViewBatchQuery(
    {
      params,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const batchDataProp = R.prop(R.__, batchData);
  const status = batchDataProp('status');

  const hasAffiliateBatchOwner = affiliates.some(({ customerNameShort }) =>
    R.equals(customerNameShort, R.path(['batchOwner', 'batchOwnerName'], batchData))
  );

  const hasAffiliateOriginOwner = affiliates.some(({ customerNameShort }) =>
    R.equals(customerNameShort, R.prop('originOwnerName', batchData))
  );

  const hasShowAssociatedShipment =
    hasAffiliateBatchOwner ||
    (R.path(['batchOwner', 'batchOwnerName'], batchData) === R.prop('originOwnerName', batchData) &&
      hasAffiliateOriginOwner);

  const hasEditPermissions = R.all(R.equals(true), [
    isProductShippingManage,
    isEditableStatus(status),
    hasAffiliateBatchOwner ||
      (R.path(['batchOwner', 'batchOwnerName'], batchData) ===
        R.prop('originOwnerName', batchData) &&
        hasAffiliateOriginOwner),
  ]);

  const handleEdit = () => {
    navigate('./edit', {
      state: {
        from: state?.from,
        url: state?.from === 'Batches Overview' ? -2 : '../batches/batch',
      },
    });
  };

  const handleValueChange = (isInvalid, hasNoUpdate) => {
    setHasNoUpdatesBatch(hasNoUpdate);
    setIsInvalidBatch(isInvalid);
  };

  const handleShowVolumeConfirmation = useCallback(() => {
    if (!isSavingChanges && childRefShipment.current) {
      childRefShipment.current.handleShowVolumeConfirmation();
    }
  }, [isSavingChanges]);

  const handleAddNewShipment = useCallback(() => {
    if (childRefShipment.current) {
      childRefShipment.current.handleAddNewShipment();
    }
  }, []);

  // fetching associated shipments to calculate the total volume
  const {
    data: batchShipmentsData = { shipments: [] },
    isFetching: isFetchingShipments,
    isSuccess: isShipmentsSuccess,
    refetch: refetchShipments,
  } = useGetBatchShipmentsQuery({
    params: { batchId },
  });

  const batchShipmentsDataProp = R.prop(R.__, batchShipmentsData);

  const [associatedShipments, setAssociatedShipments] = useState(
    batchShipmentsDataProp('shipments')
  );

  const hasStatusDetails = R.pipe(
    R.propOr([], 'shipments'),
    R.pluck('statusDetails'),
    R.any(s => !R.isNil(s) && String(s).trim() !== '')
  )(batchShipmentsData);

  useEffect(() => {
    if (isShipmentsSuccess) setAssociatedShipments(batchShipmentsData?.shipments);
  }, [batchShipmentsData?.shipments, isShipmentsSuccess]);

  const [updatedRows, setUpdatedRows] = useState([]);

  const [invalidShipmentVolume, setInvalidShipmentVolume] = useState(false);

  const [invalidAgreementCode, setInvalidAgreementCode] = useState(false);

  const totalVolume = R.pipe(
    R.prop('shipments'),
    R.filter(shipment => shipment.status !== 'Canceled' && shipment.status !== 'CANCELED'),
    R.pluck('volume'),
    R.sum
  )(batchShipmentsData);

  const volumeChangeMessage = R.gt(editedTotalVolume, totalVolume) ? 'increase' : 'decrease';

  const [createShipments] = useCreateShipmentsMutation();

  const [editShipment] = useEditShipmentMutation();

  const [availableDate, setAvailableDate] = useState(batchDataProp('availableDate'));

  const handleBatchSave = () => {
    setIsSavingChanges(true);
    setIsSavingNewShipments(true);

    const performBatchEdit = () => {
      if (!hasNoUpdatesBatch && childRef.current) {
        return childRef.current.handleSave();
      }
      return Promise.resolve({ data: { message: `'No batch updates'` } });
    };

    const performShipmentEdits = () => {
      if (updatedRows.length > 0) {
        const filteredShipments = R.filter(
          shipment => R.includes(shipment?.shipmentId, updatedRows),
          associatedShipments
        );

        const requestedDate = date =>
          isBefore(new Date(date), new Date(availableDate)) ? availableDate : date;

        return filteredShipments.reduce((promiseChain, shipment) => {
          return promiseChain.then(() => {
            return editShipment({
              body: {
                agreementCode: R.prop('agreementCode', shipment),
                bookingOriginId: R.prop('bookingOriginId', shipment),
                comments: R.prop('comments', shipment),
                requestedDate: requestedDate(R.prop('requestedDate', shipment)),
                shipmentId: R.prop('shipmentId', shipment),
                shipper: {
                  companyCustomerIdName: R.path(['shipper', 'shipperIdName'], shipment),
                  dataCompanyId: R.path(['batchOwner', 'dataCompanyId'], batchData),
                },
                volume: R.prop('volume', shipment),
              },
            });
          });
        }, Promise.resolve());
      }
      return Promise.resolve();
    };

    const performNewShipmentCreation = () => {
      if (newShipmentsValidated) {
        const newShipments = R.map(
          shipment => ({
            agreementCode: R.prop('agreementCode', shipment),
            bookingOriginId: R.prop('bookingOriginId', shipment),
            comments: R.prop('comments', shipment),
            locationId: R.prop('locationId', shipment),
            requestedDate: R.prop('requestedDate', shipment),
            shipper: {
              companyCustomerIdName: R.path(['shipper', 'shipperIdName'], shipment),
              dataCompanyId: R.path(['batchOwner', 'dataCompanyId'], batchData),
            },
            status: 'Unconfirmed',
            volume: R.prop('volume', shipment),
          }),
          R.filter(R.propEq('isNew', true), associatedShipments)
        );

        return createShipments({
          body: {
            batchId,
            shipments: newShipments,
          },
        });
      }
      return Promise.resolve();
    };

    performBatchEdit()
      .then(batchRes => {
        if (batchRes.error) {
          dispatch(
            createSnack({
              message: batchRes.error.data?.title,
              options: { variant: 'error' },
            })
          );
        }

        if (batchRes.data && batchRes.data.message !== `'No batch updates'`) {
          dispatch(
            createSnack({
              message: batchRes.data.message,
            })
          );
        }
        return performShipmentEdits();
      })
      .then(shipmentEditRes => {
        if (updatedRows.length > 0) {
          if (shipmentEditRes.error) {
            dispatch(
              createSnack({
                message: shipmentEditRes.error.data?.title,
                options: { variant: 'error' },
              })
            );
          } else if (shipmentEditRes && shipmentEditRes.data) {
            dispatch(
              createSnack({
                message: shipmentEditRes.data?.message,
              })
            );
          }
        }
        return performNewShipmentCreation();
      })
      .then(newShipmentsRes => {
        if (newShipmentsRes && newShipmentsRes.error) {
          dispatch(
            createSnack({
              message: `Unable to save shipping: ${newShipmentsRes.error.data.title}`,
              options: { variant: 'error' },
            })
          );
        } else if (newShipmentsRes && newShipmentsRes.data) {
          dispatch(
            createSnack({
              message: newShipmentsRes.data?.message,
            })
          );
        }
        return Promise.all([refetch(), refetchShipments()]);
      })
      .finally(() => {
        setUpdatedRows([]);
        setNewShipmentsValidated(false);
        setHasNewShipments(false);
        setShipmentsEdited(false);
        setIsSavingNewShipments(false);
        setIsSavingChanges(false);
      });
  };

  return (
    <Box sx={containerStyle}>
      <Stack sx={{ mb: '20px', mt: '10px' }}>
        <BackButton />
      </Stack>
      {isMobile && <Divider sx={{ mt: 2, mx: -2 }} />}
      <Stack
        alignItems="center"
        direction="row"
        justifyContent={{ sm: 'flex-start', xs: 'space-between' }}
        sx={{ mb: 2, mt: 2, pr: 1 }}
      >
        <Typography sx={{ lineHeight: 1, mb: 1.5, mr: 3 }} variant="h5">
          Batch Information
        </Typography>
        {hasEditPermissions && !onEditPage && (
          <IconButton onClick={handleEdit}>
            <EditIcon fontSize="small" sx={{ color: 'secondary.main' }} />
          </IconButton>
        )}
      </Stack>
      {isFetchingBatch ? (
        <Box textAlign="center" width="100%">
          <CircularProgress size={48} />
        </Box>
      ) : !R.prop('batchId', batchData) ? (
        <NoAvailableCard type="batch" />
      ) : (
        <>
          <Stack
            direction="row"
            display="flex"
            justifyContent={{ sm: 'flex-start', xs: 'space-between' }}
            sx={{
              display: 'grid',
              gridTemplateColumns: { lg: '2fr 0.5fr', xl: '1fr 1fr', xs: '1fr' },
              marginRight: -2,
            }}
          >
            <Box sx={infoContainerStyle}>
              <DataDisplayItem label="Batch ID" value={`#${batchDataProp('batchId')}`} />
              <DataDisplayItem
                label="Creation Date"
                value={formatDate(batchDataProp('creationDate'))}
              />
              <DataDisplayItem
                label="Batch Owner"
                value={R.path(['batchOwner', 'batchOwnerName'], batchData)}
              />
              <DataDisplayItem label="Access Code" value={batchDataProp('accessCode')} />
              <DataDisplayItem label="Origin Owner" value={batchDataProp('originOwnerName')} />
              <DataDisplayItem label="Ticket ID" value={batchDataProp('ticketId')} />
              <Stack spacing={0.5}>
                <Typography variant="caption">Status</Typography>
                <Tooltip placement="top" title={batchStatusTooltips[status]}>
                  <StatusChip status={status} />
                </Tooltip>
              </Stack>
              <DataDisplayItem label="Ticket Date" value={batchDataProp('ticketCloseDate')} />
              <DataDisplayItem label="Status Details" value={batchDataProp('statusDetails')} />
              <DataDisplayItem
                label="Ticket Volume"
                value={formatNumberWithoutDecimals(batchDataProp('ticketVolume'))}
              />
              {hasStatusDetails && hasAffiliateBatchOwner && (
                <Button
                  fullWidth={false}
                  onClick={() => setIsStatusDetailsDialogOpen(true)}
                  sx={{ justifySelf: 'flex-start', padding: '6px 16px' }}
                  variant="outlined"
                >
                  View Shipment Status Details
                </Button>
              )}
            </Box>
            <Box sx={infoContainerStyle}>
              <EditBatch
                batchData={batchData}
                batchId={batchId}
                batchOwner={batchDataProp('batchOwner')}
                isEditPermission={hasEditPermissions}
                onValueChange={handleValueChange}
                productId={batchDataProp('productId')}
                ref={childRef}
                setAvailableDate={setAvailableDate}
                totalVolume={totalVolume}
              />
            </Box>
          </Stack>
          {!onEditPage && <Divider sx={{ my: 3 }} />}
          {!onEditPage && (
            <Box sx={{ ...infoContainerStyle, marginRight: 10 }}>
              <DataDisplayItem
                label="Available Date"
                value={formatDate(batchDataProp('availableDate'))}
              />
              <DataDisplayItem label="Fuel Type" value={batchDataProp('fuelType')} />
              <DataDisplayItem label="Origin" value={batchDataProp('originName')} />
              <DataDisplayItem label="Oxygenate" value={batchDataProp('oxygenate')} />
              <DataDisplayItem label="Product" value={batchDataProp('productName')} />
              <DataDisplayItem label="RVP" value={batchDataProp('rvp')} />
              <DataDisplayItem label="Comments" value={batchDataProp('comment')} />
              <DataDisplayItem label="PO" value={batchDataProp('purchaseOrder')} />
            </Box>
          )}
          {hasShowAssociatedShipment && (
            <>
              <Divider sx={{ mb: { lg: 5, xs: 3 }, mt: { lg: 4, xs: 3 } }} />
              <ViewAssociatedShipments
                associatedShipments={associatedShipments}
                dataCompanyId={R.path(['batchOwner', 'dataCompanyId'], batchData)}
                handleBatchSave={handleBatchSave}
                isEditableStatus={isEditableStatus(status)}
                isEditPermission={hasEditPermissions}
                isFetchingShipments={isFetchingShipments}
                isProductShippingManage={isProductShippingManage}
                isShipmentsSuccess={isShipmentsSuccess}
                onEditPage={onEditPage}
                refetchBatchData={refetch}
                selectedDistribution={selectedDistribution}
                selectedShipment={selectedShipment}
                setAssociatedShipments={setAssociatedShipments}
                setEditedTotalVolume={setEditedTotalVolume}
                setHasNewShipments={setHasNewShipments}
                setIsSavingNewShipments={setIsSavingNewShipments}
                setNewShipmentsValidated={setNewShipmentsValidated}
                setSelectedDistribution={setSelectedDistribution}
                setSelectedShipment={setSelectedShipment}
                setShipmentsEdited={setShipmentsEdited}
                setUpdatedRows={setUpdatedRows}
                shipments={batchShipmentsDataProp('shipments')}
                updatedRows={updatedRows}
                {...batchData}
                ref={childRefShipment}
                setInvalidAgreementCode={setInvalidAgreementCode}
                setInvalidShipmentVolume={setInvalidShipmentVolume}
              />
              <EditDistributionsDrawer
                batchData={batchData}
                dataCompanyId={R.path(['batchOwner', 'dataCompanyId'], batchData)}
                onClose={setSelectedShipment}
                refetchShipments={refetchShipments}
                setSelectedShipment={setSelectedShipment}
                {...selectedShipment}
              />

              {onEditPage && hasEditPermissions && (
                <DialogActions
                  sx={{
                    alignSelf: 'start',
                    backgroundColor: 'surface.white',
                    borderTopColor: 'graphic.light',
                    bottom: 0,
                    display: 'flex',
                    flexDirection: 'row',
                    height: 68,
                    justifyContent: 'space-between',
                    left: 0,
                    position: 'fixed',
                    px: 5,
                    width: 1,
                    zIndex: 4,
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="flex-start"
                    spacing={1}
                    sx={{ mb: 1, mt: 1 }}
                  >
                    <Button
                      fullWidth={isMobile}
                      onClick={() => {
                        openCancelPopup();
                      }}
                      sx={{ borderColor: 'red', color: 'red', mt: '10px' }}
                      variant="outlined"
                    >
                      Cancel Batch
                    </Button>
                  </Stack>

                  <Stack
                    direction="row"
                    justifyContent="flex-end"
                    spacing={1}
                    sx={{ mb: 1, mt: 1 }}
                  >
                    <Button
                      fullWidth={isMobile}
                      onClick={handleAddNewShipment}
                      sx={{ mt: '10px' }}
                      variant="outlined"
                    >
                      New Shipment
                    </Button>

                    <Button
                      // enabled if
                      // 1. has batch update and valid
                      // 2. valid and new shipments validated
                      // 3. existing shipment is edited and there is no new shipment
                      disabled={
                        isSavingChanges ||
                        !(
                          (!hasNoUpdatesBatch && !isInvalidBatch && !invalidShipmentVolume) ||
                          (!isInvalidBatch && newShipmentsValidated && !invalidAgreementCode) ||
                          (shipmentsEdited && !hasNewShipments && !invalidAgreementCode) ||
                          isSavingNewShipments
                        )
                      }
                      fullWidth={isMobile}
                      onClick={() => {
                        newShipmentsValidated
                          ? handleShowVolumeConfirmation()
                          : !R.equals(totalVolume, editedTotalVolume)
                          ? setIsVolumeChangeDialogOpen(true)
                          : handleBatchSave();
                      }}
                      startIcon={isSavingChanges && <CircularProgress color="inherit" size={20} />}
                      sx={{ mt: '10px' }}
                    >
                      {isSavingChanges ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </Stack>
                </DialogActions>
              )}
            </>
          )}
          <DialogExplicitConfirmation
            confirmationMessage={`If you still want to cancel the ${dialogType}, enter the word below and click Confirm.`}
            confirmationValue="CANCEL"
            dialogOpen={isExplicitDialogOpen}
            handleCloseDialog={() => {
              setIsExplicitDialogOpen(false);
              setTimeout(() => {
                setDialogType('');
                setIdToCancel(null);
              }, 600);
            }}
            label={`Type "CANCEL"`}
            onConfirm={cancelSelectedBatch}
            title={`Are you sure you want to cancel this ${dialogType}?`}
            warningList={R.concat(additionalWarnings, warningList)}
          />
          <MDialog
            header={`You are about to ${volumeChangeMessage} the total Batch volume`}
            leftButtonAction={() => {
              setIsVolumeChangeDialogOpen(false);
            }}
            leftButtonText="Back"
            onClose={() => {
              setIsVolumeChangeDialogOpen(false);
            }}
            open={isVolumeChangeDialogOpen}
            rightButtonAction={() => {
              handleBatchSave();
              setIsVolumeChangeDialogOpen(false);
            }}
            rightButtonDisabled={isSavingChanges}
            rightButtonText="Confirm"
            slotProps={{ paper: { sx: dialogContainerStyles } }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                whiteSpaceCollapse: 'preserve',
              }}
            >
              <Grid container>
                <Typography sx={{ display: 'inline' }} variant="body1bold">
                  Current volume:
                </Typography>
                <Typography sx={{ display: 'inline' }}>
                  {` ${formatNumberWithoutDecimals(totalVolume || 0)}`} bbl
                </Typography>
              </Grid>
              <br />
              <Grid container>
                <Typography sx={{ display: 'inline' }} variant="body1bold">
                  Volume after confirm:
                </Typography>
                <Typography sx={{ display: 'inline' }}>
                  {` ${formatNumberWithoutDecimals(editedTotalVolume || 0)}`} bbl
                </Typography>
              </Grid>
            </Box>
            <Divider sx={{ mb: { lg: 5, xs: 3 }, mt: { lg: 4, xs: 3 } }} />
            <Typography sx={{ textAlign: 'center' }} variant="body1">
              {`If you still want to ${volumeChangeMessage} the total batch volume, click confirm.`}
            </Typography>
          </MDialog>
          <StatusDetailsDialog
            detailsKey="statusDetails"
            idKey="shipmentId"
            items={batchShipmentsData.shipments}
            onClose={() => setIsStatusDetailsDialogOpen(false)}
            onIdClick={id => navigate(`../batches/shipment/${id}/edit`)}
            open={isStatusDetailsDialogOpen}
            title="Shipment Status Details"
          />
        </>
      )}
    </Box>
  );
};

export default ViewBatchById;
